
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import ResetButton from '@/components/buttons/ResetButton.vue';
import { CollectionType } from '@/enums/collection-type';
import { isAnyFilterActive } from '@/helpers/filter-helper';
import { FilterCollection, FilterName } from '@/stores/filter.store';
import ToggleButton from '@/ui-components/ToggleButton.vue';

const Filter = namespace('filter');

@Component({
	name: 'FilterReleaseYear',
	components: { ResetButton, ToggleButton },
})
export default class FilterReleaseYear extends Vue {
	@Prop() collectionType: CollectionType;
	@Prop() currentFilters: FilterCollection;

	min = 1900;
	max = new Date().getFullYear();

	@Filter.Action setRange: ({
		rangeName,
		trackingKey,
		upper,
		lower,
		selectedState,
	}: {
		rangeName: 'price' | 'release_year';
		trackingKey: string;
		upper: number | null;
		lower: number | null;
		selectedState?: 1 | 0;
	}) => void;
	@Filter.Action resetFilter: (payload: { filterName: FilterName }) => void;

	get presetFiltersActive() {
		return {
			WEBAPP_RELEASEYEAR_THIS:
				this.currentFilters.release_year_from === this.max &&
				!isAnyFilterActive(['release_year_until'], this.currentFilters),
			WEBAPP_RELEASEYEAR_LAST:
				this.currentFilters.release_year_from === this.max - 1 &&
				this.currentFilters.release_year_until === this.max - 1,
		};
	}

	get value(): IonRangeDualKnobValue {
		return {
			lower: this.currentFilters.release_year_from || this.min,
			upper: this.currentFilters.release_year_until || this.max,
		};
	}

	setPreset(value: IonRangeDualKnobValue & { key: string }) {
		const lower = this.currentFilters.release_year_from === value.lower ? null : value.lower;
		const upper = this.currentFilters.release_year_until === value.upper ? null : value.upper;
		this.update({ lower, upper, key: value.key });
	}

	update(value: IonRangeDualKnobValue & { key: string }) {
		let selectedState: 1 | 0 = 1;
		if (this.isDateFilterActive(value.key)) {
			selectedState = 0;
		}
		this.setRange({
			rangeName: 'release_year',
			trackingKey: value.key!,
			upper: value.upper !== this.max ? value.upper : null,
			lower: value.lower !== this.min ? value.lower : null,
			selectedState,
		});
	}

	isDateFilterActive(key: string): boolean {
		return (
			['WEBAPP_RELEASEYEAR_LAST', 'WEBAPP_RELEASEYEAR_THIS'].includes(key) &&
			(this.presetFiltersActive.WEBAPP_RELEASEYEAR_LAST || this.presetFiltersActive.WEBAPP_RELEASEYEAR_THIS)
		);
	}

	@Watch('value')
	onValueChange(value: IonRangeDualKnobValue, oldValue: IonRangeDualKnobValue) {
		this.setSlideValue(value);
	}

	mounted() {
		// initially draw the store’s state on the rage slider (doesn’t work
		// by using :value.prop="value" (may be an ionic bug)
		// @TODO replace "any" type hint here as soon as clear how to define type
		this.setSlideValue(this.value);
	}

	changeRange(event: any) {
		const value = event.target?.value as IonRangeDualKnobValue | undefined;
		if (value === undefined) {
			return;
		}
		// TODO "same value" logic should be handled in modular range component.
		if (value.upper === this.value.upper && value.lower === this.value.lower) {
			return;
		}
		this.update({ ...value, key: 'knob' });
	}

	// :value.prop yields in $refs['range'].value being NaN, so we access it directly
	private setSlideValue(value: IonRangeDualKnobValue) {
		(this.$refs['range'] as any).value = value;
	}
}
